<!--
File: Planning/RoutineMaintenance/Plans.vue
Description: show list of Routine Maintenance Plans.
-->
<template>
  <base-data-table
    v-if="eligible"
    :loading="showSpinner"
    :pagination="pagination"
    :from="from"
    :to="to"
    :total="total"
    :btnAddAllowed="false">

    <template #table-rows>
      <base-table
        :items="queriedData"
        :headers="headers"
        :idFieldName="idFieldName"
        :sort="currentSort"
        :sortOrder="currentSortOrder">

        <template #table-actions="{ item }">
          <table-actions
            :btnExtraAllowed="isBtnAllowed('ApproveRevokeButton') && step === 'third'"
            :extraButtonClass="['md-raised', 'md-sm', item.approved_by_user_id ? 'md-primary' : 'md-success']"
            :extraButtonLabel="item.approved_by_user_id ? $t('buttons.revoke') : $t('buttons.approve')"
            :extraButtonStyle="'width: 100px;'"
            @extraButtonClicked="togglePlanApproval(item[idFieldName], `${item.deu_description} ${item.work_category_description}`, !item.approved_by_user_id)"

            :btnEditAllowed="isBtnAllowed('EditButton') && !item.approved_by_user_id && step === 'third' && false"
            :btnDeleteAllowed="isBtnAllowed('DeleteButton') && !item.approved_by_user_id && step === 'third'"
            @edit="viewItem(item[idFieldName])"
            @delete="deleteItem(item[idFieldName], item.description)" 
          />
        </template>
      </base-table>

      <div class="footer-table md-table">
        <table>
          <tfoot>
            <tr>
              <th v-for="item in footerTable" :key="item" class="md-table-head">
                <div class="md-table-head-container md-ripple md-disabled">
                  <div class="md-table-head-label">{{ item }}</div>
                </div>
              </th>
            </tr>
          </tfoot>
        </table>
      </div>
    </template>

    <template #editing-dialog>
      <EditForm
        v-if="showDetailsDlg"
        ref="dynamicForm"
        :formName="formName"
        v-model="itemValues"
        :actionBase="'rm_plan'"
        :idFieldName="idFieldName"
        :moduleName="'Planning'"
        :itemId="currentId"
        @close="onEditFormClose">

        <template #fields_after>
          <div :class="getSize(40)"><DepsDropdown :class="getClass('fk_deu')" 
            v-model="itemValues.fk_deu" :regionId="itemValues.fk_region" />
          </div>
        </template>
      </EditForm>
    </template>
  </base-data-table>
</template>
<script>
import { mapActions, mapGetters } from 'vuex';
import { BaseDataTable, BaseTable, TableActions, DepsDropdown } from "@/pages/Components";
import { approvedMessage } from '@/mixins/messagesMixin'
import { numFormat } from "@/store/helpers/format_helpers"
import BaseTableMixin from '@/mixins/BaseTableMixin';
import EditForm from './PlanEditForm.vue';

export default {
  name: 'routine-maintenance-plans-list',
  mixins: [BaseTableMixin],

  components: {
    BaseDataTable,
    BaseTable,
    TableActions,
    EditForm,
    DepsDropdown
  },

  data() {
    return {
      formName: 'Plans',
      eligible: true,
      idFieldName: 'routine_plan_id',
      propsToSearch: ['year', 'region_description', 'deu_description'],
      currentSort: 'treatment_count',

      selectedPlan: null,
      fk_region: null,

      historyMapping: {
        selectedRegion: 0,
        currentSort: 'treatment_count',
        currentSortOrder: 'desc',
        pagination: this.pagination,
        searchQuery: ''
      },
    }
  },

  props: {
    step: { default: 'first', type: String },
    filterParams: { default: {}, type: Object },
  },

  async mounted() {
  },

  methods: {
    ...mapActions({
      clearList: 'Planning/CLEAR_RM_PLAN_LIST',  // Called from the BaseTableMixin's mount()
      loadList: 'Planning/LOAD_RM_PLAN_LIST',
      delete: 'Planning/DELETE_RM_PLAN',
      approvePlan: 'Planning/APPROVE_RM_PLAN',
      cancelPlan: 'Planning/CANCEL_RM_PLAN',
    }),

    toggleSpinner(state) {
      this.showSpinner = state
    },

    async onEditFormClose() {
      this.showDetailsDlg = false;
      await this.reloadData();
      //console.log(JSON.stringify(this.tableData))
    },

    async togglePlanApproval(id, name, status) {
      if (this.step !== 'third') return;

      this.toggleSpinner(true);
      const action = status ? this.approvePlan : this.cancelPlan;

      try {
        await action(id);
      } finally {
        this.toggleSpinner(false);
      }
      await this.reloadData();
      await approvedMessage(status, name);

      this.highlightRow(id);
    },

  },

  computed: {
    ...mapGetters('Planning', ['rmPlansWithPrefix', 'rmPlansSummary']),

    tableData() {
      return this.rmPlansWithPrefix(this.filterParams?.year, this.$t('road_network.dep'), this.$t('buttons.no'));
    },

    headers() {
      return [
        { header: 'stdCols.year', key: 'year', sortable: true, width: 10 },
        { header: 'road_network.region', key: 'region_description', sortable: true, width: 10 },
        { header: 'road_network.dep', key: 'deu_description', sortable: true, width: 10 },
        { header: 'stdCols.type', key: 'work_category_description', sortable: true, width: 22 },
        { header: 'budget.total_cost', key: 'total_cost', sortable: true, num: true, digits: 2, width: 13 },
        { header: 'budget.works_count', key: 'treatment_count', sortable: true, num: true, digits: 0, width: 11 },
        { header: 'label.approved', key: 'approved_at', sortable: true, date: true, width: 10 },
      ]
    },

    itemValues() {
      const currItem = this.tableData.find(item => item.routine_plan_id == this.currentId);
      return {
        year: currItem.year,
        fk_region: currItem.fk_region,
        fk_deu: currItem.fk_deu,
        fk_work_category: currItem.fk_work_category,
      };
    },

    footerTable() {
      const total = this.rmPlansSummary(this.filterParams?.year);

      return [
        this.$t('label.total'),
        `${this.$t('budget.plans_count')}: ${numFormat(total.totalRecords, 0)}`,
        `${this.$t('budget.treatment_types_count')}: ${numFormat(total.totalTreatmentTypes, 0)}`,
        `${this.$t('budget.amount')}: ${numFormat(total.totalAmount)}`
      ];
    }
  },

  watch: {
    async step(newValue, oldValue) {
      if (newValue !== oldValue && newValue === 'third') {
        await this.reloadData();
      };
    },

    filterParams: {
      async handler(value) {
        this.payload = {
          year: value?.year,
          region_id: value?.fk_region  || 0,
          deu_id: value?.fk_deu || 0,
          work_category_id: value?.fk_work_category || 0,
        };
        await this.reloadData();
      },
      deep: true
    },
  }
}
</script>
<style lang="scss" scoped>
@import '@/mixins/BaseTable.scss';

// Bulletproof table styling that Vue Material cannot override
::v-deep .md-table {
  table-layout: fixed !important;
  width: 100% !important;
  border-collapse: separate !important;
  border-spacing: 0 !important;
  
  // Force table structure
  table {
    table-layout: fixed !important;
    width: 100% !important;
    border-collapse: separate !important;
  }
  
  // Column widths for approval table - 8 columns total
  // Year: 10%, Region: 12%, DEU: 12%, Type: 22%, Total: 13%, Works: 11%, Approved: 10%, Actions: 10%
  colgroup {
    col:nth-child(1) { width: 10% !important; }
    col:nth-child(2) { width: 12% !important; }
    col:nth-child(3) { width: 12% !important; }
    col:nth-child(4) { width: 22% !important; }
    col:nth-child(5) { width: 13% !important; }
    col:nth-child(6) { width: 11% !important; }
    col:nth-child(7) { width: 10% !important; }
    col:nth-child(8) { width: 10% !important; }
  }
  
  // Lock all cell widths with triple specification
  th, td {
    box-sizing: border-box !important;
    padding: 8px !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    vertical-align: middle !important;
    border: none !important;
    
    &:nth-child(1) { 
      width: 10% !important; 
      max-width: 10% !important; 
      min-width: 10% !important; 
      text-align: center !important;
    }
    &:nth-child(2) { 
      width: 12% !important; 
      max-width: 12% !important; 
      min-width: 12% !important; 
      text-align: left !important;
    }
    &:nth-child(3) { 
      width: 12% !important; 
      max-width: 12% !important; 
      min-width: 12% !important; 
      text-align: left !important;
    }
    &:nth-child(4) { 
      width: 22% !important; 
      max-width: 22% !important; 
      min-width: 22% !important; 
      text-align: left !important;
    }
    &:nth-child(5) { 
      width: 13% !important; 
      max-width: 13% !important; 
      min-width: 13% !important; 
      text-align: right !important;
    }
    &:nth-child(6) { 
      width: 11% !important; 
      max-width: 11% !important; 
      min-width: 11% !important; 
      text-align: right !important;
    }
    &:nth-child(7) { 
      width: 10% !important; 
      max-width: 10% !important; 
      min-width: 10% !important; 
      text-align: center !important;
    }
    &:nth-child(8) { 
      width: 10% !important; 
      max-width: 10% !important; 
      min-width: 10% !important; 
      text-align: left !important;
    }
  }
  
  // Header styling
  .md-table-head {
    background-color: #f8f9fa !important;
    border-bottom: 1px solid #dee2e6 !important;
    font-weight: 600 !important;
    font-size: 12px !important;
    color: #495057 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.5px !important;
    height: 44px !important;
  }
  
  .md-table-head-container {
    padding: 8px !important;
    display: flex !important;
    align-items: center !important;
    height: 100% !important;
  }
  
  .md-table-head-label {
    font-size: 11px !important;
    font-weight: 600 !important;
    color: #6c757d !important;
    text-transform: uppercase !important;
    letter-spacing: 0.5px !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    width: 100% !important;
  }
  
  // Force header alignment for each column specifically
  th {
    &:nth-child(1) .md-table-head-container { justify-content: center !important; text-align: center !important; }
    &:nth-child(2) .md-table-head-container { justify-content: flex-start !important; text-align: left !important; }
    &:nth-child(3) .md-table-head-container { justify-content: flex-start !important; text-align: left !important; }
    &:nth-child(4) .md-table-head-container { justify-content: flex-start !important; text-align: left !important; }
    &:nth-child(5) .md-table-head-container { justify-content: flex-end !important; text-align: right !important; }
    &:nth-child(6) .md-table-head-container { justify-content: flex-end !important; text-align: right !important; }
    &:nth-child(7) .md-table-head-container { justify-content: center !important; text-align: center !important; }
    &:nth-child(8) .md-table-head-container { justify-content: flex-start !important; text-align: left !important; }
    
    &:nth-child(1) .md-table-head-label { text-align: center !important; }
    &:nth-child(2) .md-table-head-label { text-align: left !important; }
    &:nth-child(3) .md-table-head-label { text-align: left !important; }
    &:nth-child(4) .md-table-head-label { text-align: left !important; }
    &:nth-child(5) .md-table-head-label { text-align: right !important; }
    &:nth-child(6) .md-table-head-label { text-align: right !important; }
    &:nth-child(7) .md-table-head-label { text-align: center !important; }
    &:nth-child(8) .md-table-head-label { text-align: left !important; }
  }
  
  // Data row styling
  tbody tr {
    border-bottom: 1px solid #f1f3f4 !important;
    
    &:hover {
      background-color: #f8f9fa !important;
    }
    
    td {
      padding: 12px 8px !important;
      font-size: 13px !important;
      color: #212529 !important;
      border: none !important;
      vertical-align: middle !important;
    }
  }
  
  // Action buttons styling
  .md-button {
    min-width: 32px !important;
    height: 32px !important;
    margin: 0 2px !important;
    font-size: 11px !important;
    
    &.md-sm {
      min-width: 80px !important;
      height: 28px !important;
      margin: 0 !important;
      float: left !important;
    }
    
    .md-icon {
      font-size: 16px !important;
    }
  }
  
  // Force Actions column content to align left
  td:nth-child(8) {
    text-align: left !important;
    
    .table-actions {
      display: flex !important;
      justify-content: flex-start !important;
      align-items: center !important;
      text-align: left !important;
    }
  }
  
  // Target table-actions component directly
  ::v-deep .table-actions {
    display: flex !important;
    justify-content: flex-start !important;
    align-items: center !important;
    text-align: left !important;
    width: 100% !important;
  }
  
  // Target the actual TableActions component container
  ::v-deep .cell-actions {
    display: flex !important;
    justify-content: flex-start !important;
    align-items: center !important;
    text-align: left !important;
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
  }
  
  // NUCLEAR OPTION - Target everything in the actions column with maximum specificity
  ::v-deep td:nth-child(8) {
    text-align: left !important;
    padding-left: 8px !important;
    
    // Force the container to left align
    .cell-actions {
      display: block !important;
      text-align: left !important;
      width: 100% !important;
      margin: 0 !important;
      padding: 0 !important;
    }
    
    // Target MD buttons directly
    .md-button {
      float: left !important;
      clear: left !important;
      margin: 0 0 2px 0 !important;
      display: block !important;
      
      &.md-raised {
        float: left !important;
        clear: none !important;
        margin: 0 !important;
      }
    }
    
    // Force everything to left
    * {
      text-align: left !important;
      margin-left: 0 !important;
      margin-right: auto !important;
    }
  }
}

// Empty state styling
::v-deep .md-table-empty-state {
  .md-table-row td {
    text-align: center !important;
    padding: 60px 20px !important;
    color: #6c757d !important;
    font-size: 14px !important;
    border: none !important;
  }
}

// Footer styling
.footer-table {
  margin-top: 16px !important;
  
  .md-table-head {
    background-color: #e9ecef !important;
    border-top: 2px solid #dee2e6 !important;
    border-bottom: 1px solid #dee2e6 !important;
    font-weight: 700 !important;
    color: #495057 !important;
  }
  
  .md-table-head-label {
    font-size: 12px !important;
    font-weight: 700 !important;
    color: #495057 !important;
  }
}

// Override any Vue Material dynamic width calculations
::v-deep .md-table table,
::v-deep .md-table thead,
::v-deep .md-table tbody,
::v-deep .md-table tr,
::v-deep .md-table th,
::v-deep .md-table td {
  display: table !important;
  table-layout: fixed !important;
  width: 100% !important;
}

::v-deep .md-table th,
::v-deep .md-table td {
  display: table-cell !important;
}
</style>